import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { pushBackHandler, runTopBackHandler } from "@/lib/backButton";
import { appPlatform } from "@/lib/platform";

export const LEAVE_GUARD_MS = 2000;
const KEY = "leaveGuard";

// « Encore une ? » : le 1er Retour est retenu (onStay), un 2e Retour dans les 2 s laisse vraiment partir.
// Android natif : pile backButton (Capacitor). iOS natif (geste bord d'écran) + web mobile : entrée d'historique tampon.
export function useLeaveGuard(enabled, onStay) {
  const nav = useNavigate();
  const location = useLocation();
  const [hint, setHint] = useState(false);
  const lastRef = useRef(0);
  const timer = useRef(null);
  const stayRef = useRef(onStay);
  stayRef.current = onStay;
  const android = appPlatform() === "android";

  const hold = () => {
    lastRef.current = Date.now();
    stayRef.current?.();
    setHint(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setHint(false), LEAVE_GUARD_MS);
  };
  const secondPress = () => Date.now() - lastRef.current < LEAVE_GUARD_MS;
  useEffect(() => () => clearTimeout(timer.current), []);

  // Android : true = on reste ; false = NativeBackButton fait nav(-1)
  useEffect(() => {
    if (!enabled || !android) return undefined;
    return pushBackHandler(() => {
      if (secondPress()) return false;
      hold();
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, android]);

  // Historique : une entrée tampon (même URL) est empilée ; la voir disparaître = Retour pressé.
  const guarded = !!location.state?.[KEY];
  const prevGuarded = useRef(null);
  const armedRef = useRef(false);
  useEffect(() => {
    if (!enabled || android) return;
    const was = prevGuarded.current;
    prevGuarded.current = guarded;
    if (guarded) { armedRef.current = true; return; }
    const arm = () => { armedRef.current = true; nav(location.pathname + location.search, { state: { ...(location.state || {}), [KEY]: true } }); };
    if (was === true) {
      armedRef.current = false;
      if (runTopBackHandler()) { arm(); return; }      // un panneau (commentaires…) s'est fermé : on réarme sans rien d'autre
      if (secondPress()) { nav(-1); return; }
      hold();
    }
    if (!armedRef.current) arm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, android, guarded]);

  return hint;
}
